/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState, store } from "..";
import { authenticationService, edgeService } from "../../services";
import { Edge, EdgeWithPool } from "../../types/edge";
import { EdgePool } from "../../types/EdgePool";
import { fillTimeSeriesData } from "~/views/subscribers/graphs/graphHelper";
import { MetricsQuery } from "~/services/APIServices";
import { observeStoreChange } from "../utils";
import { selectCompareEnable } from "./utilitySlice";
import { MetricsClient } from "~/sw/metrics/metricClient";
import { normalizeTime } from "~/helpers/utils";
import { EdgeUpgrade } from "~/types/AvailableUpgrades";
import { Tunnel } from "~/types/Tunnel";

export const edgeMetricWorker = new MetricsClient();
export const edgeMetricCompareWorker = new MetricsClient();

type initState = {
  open: boolean;
  current: Edge | EdgeWithPool | null;
  data: Edge[];
  loading: boolean;
  edgePoolsLoading: boolean;
  showErrors: boolean;
  edgePools: EdgePool[];
  edgesWithPool: EdgeWithPool[];
  alarmTrigger: boolean;
  currentEdgePool: EdgePool | null;
  upgrades: EdgeUpgrade[];

  support: {
    open: boolean;
    dialog: boolean;
    type: "enable" | "disable" | null;
    id: string | null;
    isRunning: boolean;
  };

  kpiExport: {
    open: boolean;
    operationID: string | null;
    isRunning: boolean;
  };
  kpiOptions: {
    plmn: string;
    technology: string;
    templateId: string;
    cellId: number | string;
  };

  pcap: { operationID: string | null; }
  techBundle: { operationID: string | null; }

  metricsLoading: boolean;
  metricsError: string | null;

  metrics: {
    [key: string]: [number, number][];
  };

  compareMetrics: Record<string, [number, number][]>;

  aggregatedMetrics: {
    [key: string]: [number, number][];
  };

  tunnels: {
    loading: boolean;
    data: Tunnel[];
    current: Tunnel | null;
    open: boolean;
    isEditing: boolean;
  };

  hasPartnerRadioManager: boolean;
};

export const initialState: initState = {
  open: false,
  current: null,
  loading: true,
  edgePoolsLoading: true,
  data: [],
  edgesWithPool: [],
  showErrors: false,
  edgePools: [],
  currentEdgePool: null,
  alarmTrigger: false,
  upgrades: [],

  support: {
    open: false,
    dialog: false,
    id: null,
    type: null,
    isRunning: false,
  },

  kpiExport: {
    open: false,
    operationID: null,
    isRunning: false,
  },
  kpiOptions: {
    plmn: "",
    technology: "4G",
    templateId: "",
    cellId: "1|2"
  },
  pcap: { operationID: null },
  techBundle: { operationID: null },

  metricsLoading: true,
  metricsError: null,
  metrics: {},
  compareMetrics: {},
  aggregatedMetrics: {},

  tunnels: {
    loading: false,
    data: [],
    current: null,
    open: false,
    isEditing: false,
  },

  hasPartnerRadioManager: false,
};

export const fetchEdgeMetrics = createAsyncThunk(`edge/fetchEdgeMetrics`, async (query: MetricsQuery, thunkApi) => {
  try {
    const state = thunkApi.getState() as RootState;
    const { interval, timeZone } = state.utility.time;

    query.interval = {
      startTime: normalizeTime(query.interval.startTime, query.resolution, timeZone),
      endTime: normalizeTime(query.interval.endTime, query.resolution, timeZone)
    }

    let data: typeof initialState.metrics = {};

    await edgeMetricWorker.initialize('edges', query, authenticationService.currentUserValue!.token, (id, metrics) => {
      data = metrics as typeof initialState.metrics;
    }, { noPolling: interval === 'custom', interval: 30 });


    return data;
  } catch (err: any) {
    const errorMsg = err.errors[0].message;
    return thunkApi.rejectWithValue(errorMsg);
  }
});

export const fetchEdgeMetricsCompare = createAsyncThunk(`edge/fetchEdgeMetricsCompare`, async (query: MetricsQuery, thunkApi) => {
  try {
    const state = thunkApi.getState() as RootState;
    const { interval, timeZone } = state.utility.time;

    query.interval = {
      startTime: normalizeTime(query.interval.startTime, query.resolution, timeZone),
      endTime: normalizeTime(query.interval.endTime, query.resolution, timeZone)
    }

    let data: typeof initialState.metrics = {};

    await edgeMetricCompareWorker.initialize('edges', query, authenticationService.currentUserValue!.token, (id, metrics) => {
      data = metrics as typeof initialState.metrics;
    }, { noPolling: interval === 'custom', interval: 30 });
    return data;
  } catch (err: any) {
    const errorMsg = err.errors[0].message;
    return thunkApi.rejectWithValue(errorMsg);
  }
});

export const fetchEdgeMetricsAggregate = createAsyncThunk(
  `edge/fetchEdgeMetricsAggregate`,
  edgeService.getAggregateMetrics
);

export const fetchEdges = createAsyncThunk("edge/fetchEdges", async (query?: { timestamp?: number }) => {
  const timestamp = query?.timestamp;
  try {
    const query = timestamp ? { timestamp } : {};
    const data = await edgeService.getEdges(query);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
});

export const fetchEdgePools = createAsyncThunk("edge/fetchEdgePools", async (query?: { timestamp?: number }) => {
  const timestamp = query?.timestamp;
  try {
    const queryString = timestamp ? { timestamp } : {};
    const edgePools = await edgeService.getEdgePool(queryString);
    return edgePools;
  } catch (error) {
    console.log(error);
    throw error;
  }
});

export const fetchEdgeUpgrades = createAsyncThunk("edge/fetchEdgeUpgrades", async () => {
  try {
    const edgeUpgrades = await edgeService.getEdgeUpgrades();
    return edgeUpgrades;
  } catch (error) {
    console.log(error);
    throw error;
  }
});

export const fetchTunnels = createAsyncThunk(
  "edge/fetchTunnels",
  async (edgeId: string) => {
    try {
      const data = await edgeService.getTunnels(edgeId);
      return data;
    } catch (error) {
      console.error("Error fetching tunnels:", error);
      throw error;
    }
  }
);

export const fetchHasPartnerRadioManager = createAsyncThunk(
  "edge/fetchHasPartnerRadioManager",
  async () => {
    const res = await edgeService.getPartnerRadioManagers();
    return (res.data?.length ?? 0) > 0;
  }
);

export const edgeSlice = createSlice({
  name: "edge",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setShowErrors: (state, action: PayloadAction<boolean>) => {
      state.showErrors = action.payload;
    },
    setValues: (state, action: PayloadAction<Edge | EdgeWithPool | null>) => {
      state.current = action.payload;
    },
    setData: (state, action: PayloadAction<Edge[]>) => {
      state.data = action.payload;
    },

    setSupportPopUpOpen: (state, action: PayloadAction<boolean>) => {
      state.support.open = action.payload;
    },
    setSupportDialogOpen: (
      state,
      action: {
        payload: {
          open: boolean;
          type: "enable" | "disable" | null;
        };
      }
    ) => {
      state.support.dialog = action.payload.open;
      state.support.type = action.payload.type;
    },
    setSupportId: (state, action: PayloadAction<string | null>) => {
      state.support.id = action.payload;
    },
    setSupportIsRunning: (state, action: PayloadAction<boolean>) => {
      state.support.isRunning = action.payload;
    },
    setSupport(state, action: PayloadAction<{ open: boolean; id: string | null; isRunning: boolean }>) {
      state.support.open = action.payload.open;
      state.support.id = action.payload.id;
      state.support.isRunning = action.payload.isRunning;
    },
    setCurrentPool(state, action: PayloadAction<EdgePool | null>) {
      state.currentEdgePool = action.payload;
    },

    setKPIExportOpen(state, action: PayloadAction<boolean>) {
      state.kpiExport.open = action.payload;
    },
    setKPIExportID(state, action: PayloadAction<string | null>) {
      state.kpiExport.operationID = action.payload;
    },
    setKPIExportRunning(state, action: PayloadAction<boolean>) {
      state.kpiExport.isRunning = action.payload;
    },

    // kpi options
    setKPIPlmn(state, action: PayloadAction<string>) {
      state.kpiOptions.plmn = action.payload;
    },

    setKPITechnology(state, action: PayloadAction<string>) {
      state.kpiOptions.technology = action.payload;
    },
    setKPITemplateId(state, action: PayloadAction<string>) {
      state.kpiOptions.templateId = action.payload;
    },
    clearCompareMetrics(state) {
      state.compareMetrics = {}
    },

    // Packet Capture Actions
    setPcapOperationID(state, action: PayloadAction<string | null>) {
      state.pcap.operationID = action.payload;
    },

    // Tech Bundle
    setTechBundleOperationID(state, action: PayloadAction<string | null>) {
      state.techBundle.operationID = action.payload;
    },

    // Tunnel actions
    setTunnelDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.tunnels.open = action.payload;
    },
    setTunnelEditing: (state, action: PayloadAction<boolean>) => {
      state.tunnels.isEditing = action.payload;
    },
    setSelectedTunnel: (state, action: PayloadAction<Tunnel | null>) => {
      state.tunnels.current = action.payload;
    },
    resetTunnelState: (state) => {
      state.tunnels.data = [];
      state.tunnels.current = null;
    },

  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEdges.fulfilled, (state, action) => {
        const withSupportStatus = SyncGoingOutOfSupportWithUpgrades(action.payload, state.upgrades);
        const { alarmTriggered, updatedEdges, enrichedEdges } = SyncEdgeWithPool(withSupportStatus, state.edgePools);
        // enrichedEdges carries the pool fields state.data used to receive via in-place mutation
        state.data = enrichedEdges;
        state.edgesWithPool = updatedEdges;
        state.alarmTrigger = alarmTriggered;
        state.loading = false;

        // sync the current subscriber with the new data
        if (state.current && !state.open) {
          const current = state.edgesWithPool.find((item) => item.id === state.current?.id);
          if (current) {
            state.current = current;
          } else {
            state.current = null;
          }
        }
      })
      .addCase(fetchEdges.rejected, (state, action) => {
        state.loading = false;
        console.log(action);
      })
      .addCase(fetchEdgePools.fulfilled, (state, action) => {
        state.edgePools = action.payload;
        const withSupportStatus = SyncGoingOutOfSupportWithUpgrades(state.data, state.upgrades);
        const { alarmTriggered, updatedEdges, enrichedEdges } = SyncEdgeWithPool(withSupportStatus, action.payload);
        state.data = enrichedEdges;
        state.edgesWithPool = updatedEdges;
        state.alarmTrigger = alarmTriggered;
        state.edgePoolsLoading = false;
      })
      .addCase(fetchEdgePools.rejected, (state, action) => {
        console.log(action);
        state.edgePoolsLoading = false;
      })

      .addCase(fetchEdgeMetrics.pending, (state, action) => {
        if (action.meta.arg._showLoading) state.metricsLoading = true;
      })
      .addCase(fetchEdgeMetrics.fulfilled, (state, action) => {
        const data = action.payload;
        Object.keys(data).forEach((key) => {
          state.metrics[key] = data[key];
        });
        state.metricsError = null;
        state.metricsLoading = false;
      })
      .addCase(fetchEdgeMetricsCompare.fulfilled, (state, action) => {
        const data = action.payload;
        Object.keys(data).forEach((key) => {
          state.compareMetrics[key] = data[key];
        });
      })
      .addCase(fetchEdgeMetrics.rejected, (state, action) => {
        state.metricsLoading = false;
        state.metricsError = action.payload as string;
      })
      .addCase(fetchEdgeMetricsAggregate.pending, (state) => {
        state.metricsLoading = true;
      })
      .addCase(fetchEdgeMetricsAggregate.fulfilled, (state, action) => {
        const data = action.payload;
        data.forEach((obj) => {
          if (!obj.metricData.length) {
            state.aggregatedMetrics[obj.metric] = [];
            return [];
          }
          try {
            const metricData = fillTimeSeriesData({
              data: obj.metricData,
              startTime: action.meta.arg.interval.startTime,
              endTime: action.meta.arg.interval.endTime,
              interval: action.meta.arg.resolution,
              fillType: "previous",
            });
            state.aggregatedMetrics[obj.metric] = metricData;
          } catch (e) {
            console.error("Error in fillTimeSeries", e, obj.metric, obj.metricData);
            const metricData = obj.metricData.length ? obj.metricData : [];
            state.aggregatedMetrics[obj.metric] = metricData;
          }
          state.metricsLoading = false;
        });
      })
      .addCase(fetchEdgeMetricsAggregate.rejected, (state, action) => {
        console.error(action.error);
        state.metricsLoading = false;
        throw action.error;
      })
      // fetch edge upgrades
      .addCase(fetchEdgeUpgrades.fulfilled, (state, action) => {
        state.upgrades = action.payload;
        const withSupportStatus = SyncGoingOutOfSupportWithUpgrades(state.data, action.payload);
        // SyncEdgeWithPool no longer mutates its input in place (MCS-3969), so its
        // pool-enriched result must be captured and re-assigned to state.data instead of
        // relying on the old aliasing side effect.
        const { alarmTriggered, updatedEdges, enrichedEdges } = SyncEdgeWithPool(withSupportStatus, state.edgePools);
        state.data = enrichedEdges;
        state.edgesWithPool = updatedEdges;
        state.alarmTrigger = alarmTriggered;
        if (state.current && !state.open) {
          const current = state.edgesWithPool.find((item) => item.id === state.current?.id);
          state.current = current ?? null;
        }
      })
      .addCase(fetchEdgeUpgrades.rejected, (state, action) => {
        console.error(action.error);
        state.upgrades = [];
      })
      // Tunnel thunks
      .addCase(fetchTunnels.pending, (state) => {
        state.tunnels.loading = true;
      })
      .addCase(fetchTunnels.fulfilled, (state, action) => {
        state.tunnels.loading = false;
        state.tunnels.data = action.payload || [];
      })
      .addCase(fetchTunnels.rejected, (state) => {
        state.tunnels.loading = false;
      })
      .addCase(fetchHasPartnerRadioManager.fulfilled, (state, action) => {
        state.hasPartnerRadioManager = action.payload;
      })
      .addCase(fetchHasPartnerRadioManager.rejected, (state) => {
        state.hasPartnerRadioManager = false;
      })
  },
});

export const selectEdgesUsingDnn = (dnnId: string | undefined) => (state: RootState) => {
  const edges = state.edge.data;

  // Filter edges that use the given DNN
  const edgesUsingDnn = edges.filter((edge) =>
    edge.fabrics?.fabricInterfaces?.some((fabric) =>
      fabric.networks?.some((network) => network.dnnId === dnnId)
    )
  );
  return edgesUsingDnn.map((edge) => edge.name);
};


export const edgeActions = edgeSlice.actions;
export default edgeSlice.reducer;

export const edgeOpen = (state: RootState) => state.edge.open;
export const showErrors = (state: RootState) => state.edge.showErrors;
export const edgeState = (state: RootState) => state.edge.current;
export const edgeLoading = (state: RootState) => state.edge.loading;

export const edgeData = (state: RootState) => state.edge.data;
export const edgePools = (state: RootState) => state.edge.edgePools;
export const edgePoolsLoading = (state: RootState) => state.edge.edgePoolsLoading;
export const edgeCurrentPool = (state: RootState) => state.edge.currentEdgePool;
export const edgeEdgesWithPool = (state: RootState) => state.edge.edgesWithPool;
export const edgeAlarmTrigger = (state: RootState) => state.edge.alarmTrigger;
export const edgeUpgrades = (state: RootState) => state.edge.upgrades;

export const edgeSupportPopUpOpen = (state: RootState) => state.edge.support.open;
export const edgeSupportDisableDialogOpen = (state: RootState) =>
  state.edge.support.dialog && state.edge.support.type === "disable";
export const edgeSupportEnableDialogOpen = (state: RootState) =>
  state.edge.support.dialog && state.edge.support.type === "enable";
export const edgeSupportType = (state: RootState) => state.edge.support.type;
export const edgeSupportId = (state: RootState) => state.edge.support.id;
export const edgeSupportIsRunning = (state: RootState) => state.edge.support.isRunning;

export const edgeMetricLoading = (state: RootState) => state.edge.metricsLoading;
export const edgeMetricsError = (state: RootState) => state.edge.metricsError;
export const edgeMetrics = (state: RootState) => state.edge.metrics;
export const edgeMetric = (metric: string) => (state: RootState) => state.edge.metrics[metric];
export const edgeCompareMetrics = (state: RootState) => state.edge.compareMetrics;
export const edgeKPIOption = (state: RootState) => state.edge.kpiOptions;

export const edgeAggregatedMetric = (metric: string) => (state: RootState) => state.edge.aggregatedMetrics[metric];
export const has5gEdge = (state: RootState) => state.edge.data.some((edge) => edge.networkType === "5G");
export const selectHasPartnerRadioManager = (state: RootState) => state.edge.hasPartnerRadioManager;

export const tunnelData = (state: RootState) => state.edge.tunnels.data;
export const tunnelState = (state: RootState) => state.edge.tunnels.current;
export const tunnelLoading = (state: RootState) => state.edge.tunnels.loading;
export const tunnelOpen = (state: RootState) => state.edge.tunnels.open;
export const tunnelIsEditing = (state: RootState) => state.edge.tunnels.isEditing;

/**
 * Syncs edges with their corresponding pools and upgrades information
 * @param edges - Array of Edge objects to be synced
 * @param pools - Array of EdgePool objects to sync with
 * @returns Object containing updated edges array and alarm trigger status
 */
function SyncEdgeWithPool(edges: Edge[], pools: EdgePool[]) {
  let alarmTriggered = false;

  // Map through edges and enhance with pool information.
  // Shallow-copy each edge instead of enriching it in place: this helper also receives
  // frozen objects already in state (fetchEdgePools/fetchEdgeUpgrades pass state.data),
  // and identity-preserving update paths (entity-adapter migration, MCS-3969) make that
  // the norm — in-place Object.assign would throw on them.
  const enrichedEdges = edges.map((item, i) => {
    const edge = { ...item } as Edge & EdgeWithPool;
    // Check for network mode changes that trigger alarms
    // Only check if alarm hasn't been triggered and not the first edge
    if (!alarmTriggered && i > 0 && edges[i - 1].networkMode !== edge.networkMode) {
      alarmTriggered = true;
    }

    // Find matching pool for current edge
    const pool = pools.find((pool) =>
      pool.edgeList.find((edgeList) => edgeList.id === edge.id)
    );

    if (pool) {
      // Enhance edge with pool information
      Object.assign(edge, {
        edgeGroupId: pool.edgeGroupId,
        edgePoolname: pool.name,
        edgePoolId: pool.id,
        edgeRole: pool.edgeList.find((edgeList) => edgeList.id === edge.id)?.role
      });
    } else {
      // Set default values for standalone edges
      Object.assign(edge, {
        edgePoolname: "Standalone Edges",
        edgePoolId: undefined,
        edgeRole: "Standalone"
      });
    }

    return edge;
  });

  // Add placeholder entries for empty pools
  const updatedEdges: EdgeWithPool[] = [...enrichedEdges];
  pools.forEach((pool) => {
    const poolExists = updatedEdges.some(edge => edge.edgePoolname === pool.name);
    if (!poolExists) {
      updatedEdges.push({
        edgePoolname: pool.name,
        isEmptyPool: true,
      });
    }
  });

  return {
    updatedEdges,
    enrichedEdges,
    alarmTriggered,
  };
}

function SyncGoingOutOfSupportWithUpgrades(edges: Edge[], upgrades: EdgeUpgrade[]) {
  const goingOutOfSupportByEdgeId = new Map(
    upgrades
      .filter((upgrade) => typeof upgrade.goingOutOfSupport === "boolean")
      .map((upgrade) => [upgrade.id, upgrade.goingOutOfSupport])
  );

  return edges.map((edge) => {
    if (!edge.id || !goingOutOfSupportByEdgeId.has(edge.id)) {
      return edge;
    }

    return {
      ...edge,
      goingOutOfSupport: goingOutOfSupportByEdgeId.get(edge.id),
    };
  });
}


// clear compare metrics when the compare is closed
setTimeout(() => {
  observeStoreChange(selectCompareEnable, (state) => {
    if (!state) {
      store.dispatch(edgeActions.clearCompareMetrics())
    }
  })

  edgeMetricWorker.eventEmitter().on('worker:data', async (id, workerID, metrics) => {
    store.dispatch({ type: 'edge/fetchEdgeMetrics/fulfilled', payload: metrics });
  });
  edgeMetricCompareWorker.eventEmitter().on('worker:data', async (id, workerID, metrics) => {
    store.dispatch({ type: 'edge/fetchEdgeMetricsCompare/fulfilled', payload: metrics });
  });
});
